import { useRef, useState } from "react";
import Avatar from "./UI/Avatar";
import Radio from "./UI/Radio";
import { useTranslate } from "@core/hooks";
import type { TLanguage } from "@core/types/TLanguage";
import { useToast } from "@core/components/UI/Toast/use-toast";
import { useAuth } from "@modules/auth/hooks/useAuth";
import { useLogout } from "@modules/auth/hooks/useLogout";
import ProfileSettings from "./ProfileSettings";
import useClickOutside from "@core/utils/clickOutside/useClickOutside";
import PasswordSettings from "./PasswordSettings";
import Icon from "./UI/Icon";
import type { IconKey } from "@core/assets";
import { cn } from "@core/utils";
import { createPortal } from "react-dom";

type SettingChild = {
  label: string;
  isSelected: boolean;
  action: () => void;
};

type SettingItem = {
  id: number;
  icon: IconKey;
  label: string;
  desc?: string;
  action?: () => void;
  disabled?: boolean;
  destructive?: boolean;
  children?: SettingChild[];
};

function ProfileMenu() {
  const [isOpen, setIsOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isPwdOpen, setIsPwdOpen] = useState(false);

  const { user } = useAuth();
  const { logout, isLoggingOut } = useLogout();

  const { t, i18n } = useTranslate("auth");
  const { toast } = useToast();

  const triggerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useClickOutside([triggerRef, menuRef], () => {
    if (isOpen) setIsOpen(false);
  });

  const toggleMenu = () => setIsOpen((prev) => !prev);

  const handleChangeLanguage = async (selectedLanguage: TLanguage) => {
    try {
      await i18n.changeLanguage(selectedLanguage);
    } catch {
      toast({
        title: t("profile-menu.language-switch-failed"),
        description: t("profile-menu.language-switch-failed-desc"),
        variant: "destructive",
      });
    }
  };

  const settings: SettingItem[] = [
    {
      id: 1,
      icon: "square-user",
      label: t("profile-menu.profile-settings"),
      desc: t("profile-menu.profile-settings-desc"),
      action: () => {
        setIsProfileOpen(true);
        setIsOpen(false);
      },
    },
    {
      id: 2,
      icon: "shield",
      label: t("profile-menu.privacy-settings"),
      desc: t("profile-menu.privacy-settings-desc"),
      action: () => {
        setIsPwdOpen(true);
        setIsOpen(false);
      },
    },
    {
      id: 3,
      icon: "globe",
      label: t("profile-menu.language"),
      desc: t("profile-menu.language-desc"),
      children: [
        {
          label: t("profile-menu.language-en"),
          isSelected: i18n.language === "en",
          action: () => handleChangeLanguage("en"),
        },
        {
          label: t("profile-menu.language-fr"),
          isSelected: i18n.language === "fr",
          action: () => handleChangeLanguage("fr"),
        },
      ],
    },
    {
      id: 4,
      icon: "log-out",
      label: t("profile-menu.sign-out"),
      desc: isLoggingOut ? t("profile-menu.signing-out") : t("profile-menu.sign-out-desc"),
      action: () => {
        logout();
        setIsOpen(false);
      },
      disabled: isLoggingOut,
      destructive: true,
    },
  ];

  const initials = `${user?.first_name?.charAt(0) ?? ""}${user?.last_name?.charAt(0) ?? ""}`;

  return (
    <>
      <div ref={triggerRef}>
        {user?.picture ? (
          <img
            src={user.picture}
            alt={t("profile-menu.user-avatar-alt")}
            className="size-12 rounded-full object-cover shrink-0 cursor-pointer"
            onClick={toggleMenu}
          />
        ) : (
          <Avatar
            shape="circle"
            size="lg"
            variantColor="gradiant"
            className="cursor-pointer"
            onClick={toggleMenu}
          >
            {initials}
          </Avatar>
        )}
      </div>

      {isOpen &&
        createPortal(
          <div
            ref={menuRef}
            className="fixed right-3 top-[calc(var(--app-header-h)+8px)] w-[18.25rem] bg-white border border-neutral-200 rounded-xl shadow-lg z-50 overflow-hidden"
          >
            <div className="flex items-center gap-2 p-3 bg-neutral-50 border-b border-neutral-200">
              <Avatar size="lg" shape="circle" variantColor="gradiant">
                {initials}
              </Avatar>

              <div className="flex flex-col gap-0.5 min-w-0">
                <span className="text-sm font-medium text-secondary-900 truncate">
                  {user?.first_name} {user?.last_name}
                </span>

                <span className="text-xs text-neutral-500 truncate">
                  {user?.email}
                </span>
              </div>
            </div>

            <div className="flex flex-col p-2 gap-0.5">
              {settings.map((setting, index) => {
                const isLast = index === settings.length - 1;

                return (
                  <div key={setting.id}>
                    {isLast && (
                      <div className="border-t border-neutral-200 my-1" />
                    )}

                    <button
                      className={cn(
                        "w-full rounded-lg px-2 py-2 flex items-center gap-2 text-left transition-colors duration-200",
                        setting.disabled
                          ? "opacity-50 cursor-not-allowed"
                          : "cursor-pointer hover:bg-neutral-200",
                      )}
                      onClick={setting.action}
                      disabled={setting.disabled}
                    >
                      <Icon
                        name={setting.icon}
                        className={cn(
                          "shrink-0 text-xl",
                          setting.destructive
                            ? "text-accent-red-600"
                            : "text-neutral-500",
                        )}
                      />

                      <div className="flex flex-col items-start">
                        <span
                          className={cn(
                            "text-sm font-medium",
                            setting.destructive
                              ? "text-accent-red-600"
                              : "text-secondary-900",
                          )}
                        >
                          {setting.label}
                        </span>

                        {setting.desc && (
                          <span
                            className={cn(
                              "text-xs",
                              setting.destructive
                                ? "text-accent-red-400"
                                : "text-neutral-500",
                            )}
                          >
                            {setting.desc}
                          </span>
                        )}
                      </div>
                    </button>

                    {setting.children && (
                      <div className="flex flex-col gap-0.5 pl-8 pb-1">
                        {setting.children.map((child) => (
                          <div
                            key={child.label}
                            className={cn(
                              "flex items-center gap-2 px-2 py-1.5 rounded-lg cursor-pointer transition-colors duration-200 hover:bg-surface-hover/50",
                              child.isSelected && "bg-surface-hover/50",
                            )}
                            onClick={child.action}
                          >
                            <Radio
                              checked={child.isSelected}
                              onClickAction={child.action}
                            />

                            <span className="text-sm text-secondary-900">
                              {child.label}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>,
          document.body,
        )}

      {isProfileOpen && (
        <ProfileSettings onClose={() => setIsProfileOpen(false)} />
      )}

      {isPwdOpen && <PasswordSettings onClose={() => setIsPwdOpen(false)} />}
    </>
  );
}

export default ProfileMenu;
